import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { FaYoutube, FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa6";
import { RiTwitterXLine } from "react-icons/ri";
import { PiThreadsLogo } from "react-icons/pi";
import Logo from "../assets/goAthleteLogo.svg";
import DarkThemeLogo from "../assets/darkthemeicon.svg"; // moon or dark mode icon
import LightThemeLogo from "../assets/lightthemeicon.svg"; // sun or light mode icon

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState("light");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    localStorage.setItem("theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  return (
    <>
      {/* Mobile version */}
      <div
        className={`flex md:hidden h-[65px] px-4 items-center justify-between fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
          scrolled ? "bg-white shadow-md dark:bg-[#030929]" : ""
        }`}
      >
        {/* Logo on Left */}
        <NavLink to="/">
          <img src={Logo} alt="goAthlete Logo" className="w-[130px] h-auto drop-shadow-[0_0_10px_rgba(255,255,255,0.1)]" />
        </NavLink>

        {/* Hamburger Menu on Right */}
        <button
          className="text-2xl text-white focus:outline-none p-2 rounded-full bg-[#DA6F2B] dark:bg-[#030929] shadow-md"
          onClick={() => setSidebarOpen(true)}
        >
          ☰
        </button>
      </div>
      
      {/* Tablet version */}
      <div
        className={`hidden md:flex lg:hidden h-[70px] px-6 items-center justify-between fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
          scrolled ? "bg-white shadow-md dark:bg-[#030929]" : ""
        }`}
      >
        {/* Logo */}
        <NavLink to="/">
          <img
            src={Logo}
            alt="goAthlete Logo"
            className="w-[150px] h-auto rounded-md drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]"
          />
        </NavLink>

        {/* Condensed Nav for Tablet */}
        <div className="flex items-center gap-4">
          {/* Theme Toggle */}
          <button
            className="h-[40px] w-[40px] rounded-full bg-[#DA6F2B] dark:bg-[#030929] shadow-md flex justify-center items-center"
            onClick={toggleTheme}
          >
            <img
              src={theme === "light" ? DarkThemeLogo : LightThemeLogo}
              alt={theme === "light" ? "Dark Mode" : "Light Mode"}
              className="w-5 h-5 invert"
            />
          </button>
          
          {/* Hamburger for tablet */}
          <button
            className="text-xl text-white focus:outline-none p-2 rounded-full bg-[#DA6F2B] dark:bg-[#030929] shadow-md"
            onClick={() => setSidebarOpen(true)}
          >
            ☰
          </button>
        </div>
      </div>
      
      {/* Desktop version */}
      <div
        className={`hidden lg:flex h-[80px] p-[30px] pt-[30px] items-center justify-around fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
          scrolled ? "bg-white shadow-md dark:bg-[#0A1F35]" : "bg-white dark:bg-[#0A1F35]"
        }`}
      >
        {/* Logo */}
        <NavLink to="/">
          <img
            src={Logo}
            alt="goAthlete Logo"
            className="w-[180px] h-[50px] rounded-md drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]"
          />
        </NavLink>

        {/* Nav */}
        <nav className="px-6 py-3 rounded-full bg-[#DA6F2B] shadow-md dark:bg-[#030929] flex gap-6 text-[13px] text-white font-semibold">
          <NavLink to="/start-playing">START PLAYING</NavLink>
          <NavLink to="/get-trained">GET TRAINED</NavLink>
          <NavLink to="/where-to-play">WHERE TO PLAY?</NavLink>
          <NavLink to="/shop-sports-gear">SHOP SPORTS GEAR</NavLink>
          <NavLink to="/beyond-court">BEYOND THE COURT</NavLink>
          <NavLink to="/events">EVENTS</NavLink>
        </nav>

        {/* Buttons */}
        <div className="flex gap-[30px] items-center">
          {/* Theme Toggle */}
          <button
            className="h-[45px] w-[45px] rounded-full bg-[#DA6F2B] dark:bg-[#030929] shadow-md flex justify-center items-center"
            onClick={toggleTheme}
          >
            <img
              src={theme === "light" ? DarkThemeLogo : LightThemeLogo}
              alt={theme === "light" ? "Dark Mode" : "Light Mode"}
              className="w-5 h-5 invert"
            />
          </button>
          <NavLink to="/contact" className="px-6 py-3 rounded-full bg-[#DA6F2B] dark:bg-[#030929] text-white font-semibold transition-colors duration-300 shadow-md">
            Contact Us
          </NavLink>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-full w-[280px] bg-[#DA6F2B] dark:bg-[#030929] shadow-2xl z-50 transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex justify-between items-center p-5 border-b border-white dark:border-[#222222]">
          <h2 className="text-xl font-bold text-white">
            Menu
          </h2>
          <button
            className="text-3xl text-white"
            onClick={() => setSidebarOpen(false)}
          >
            &times;
          </button>
        </div>

        {/* Nav Links */}
        <nav className="flex flex-col p-5 gap-5 text-white font-semibold">
          <NavLink to="/start-playing" onClick={() => setSidebarOpen(false)} className="py-1.5">
            START PLAYING
          </NavLink>
          <NavLink to="/get-trained" onClick={() => setSidebarOpen(false)} className="py-1.5">
            GET TRAINED
          </NavLink>
          <NavLink to="/where-to-play" onClick={() => setSidebarOpen(false)} className="py-1.5">
            WHERE TO PLAY?
          </NavLink>
          <NavLink to="/shop-sports-gear" onClick={() => setSidebarOpen(false)} className="py-1.5">
            SHOP SPORTS GEAR
          </NavLink>
          <NavLink to="/beyond-court" onClick={() => setSidebarOpen(false)} className="py-1.5">
            BEYOND THE COURT
          </NavLink>
          <NavLink to="/events" onClick={() => setSidebarOpen(false)} className="py-1.5">
            EVENTS
          </NavLink>
          <NavLink to="/contact" onClick={() => setSidebarOpen(false)} className="py-1.5">
            CONTACT
          </NavLink>

          {/* Mobile Theme Toggle inside sidebar too */}
          <button
            className="mt-6 flex gap-3 items-center p-2 rounded-lg bg-[#DA6F2B] dark:bg-[#030929] text-white"
            onClick={toggleTheme}
          >
            <img
              src={theme === "light" ? DarkThemeLogo : LightThemeLogo}
              alt={theme === "light" ? "Dark Mode" : "Light Mode"}
              className="w-6 h-6 invert"
            />
            <span>{theme === "light" ? "Dark Mode" : "Light Mode"}</span>
          </button>
          <div className="my-5 h-px bg-gray-200 dark:bg-[#222222]" />
          {/* Quick access and legal */}
          <div className="flex flex-col gap-3 text-sm">
            <span className="text-gray-600 dark:text-white/70 font-medium">Quick Access</span>
            <div className="grid grid-cols-2 gap-3">
              <NavLink to="/start-playing" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">Start Playing</NavLink>
              <NavLink to="/get-trained" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">Get Trained</NavLink>
              <NavLink to="/where-to-play" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">Where to Play</NavLink>
              <NavLink to="/shop-sports-gear" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">Shop</NavLink>
              <NavLink to="/events" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">Events</NavLink>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <NavLink to="/company" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">Company</NavLink>
              <NavLink to="/about" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">About</NavLink>
              <NavLink to="/privacy-policy" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">Privacy</NavLink>
              <NavLink to="/terms-and-conditions" onClick={() => setSidebarOpen(false)} className="text-[#030929] dark:text-white/90 hover:underline">Terms</NavLink>
            </div>
          </div>

          {/* Social icons */}
          <div className="mt-4 flex items-center gap-4 text-[20px] text-[#030929] dark:text-white">
            <a href="#" aria-label="YouTube"><FaYoutube /></a>
            <a href="#" aria-label="Facebook"><FaFacebook /></a>
            <a href="#" aria-label="Instagram"><FaInstagram /></a>
            <a href="#" aria-label="Threads"><PiThreadsLogo /></a>
            <a href="#" aria-label="X"><RiTwitterXLine /></a>
            <a href="#" aria-label="LinkedIn"><FaLinkedin /></a>
          </div>
        </nav>
      </aside>
    </>
  );
}
